"use client";

import { useCallback, useSyncExternalStore } from "react";

const KEY = "one/rail-collapsed";
const EVENT = "one-rail-collapsed";

// Where the choice lives when storage throws (private windows, blocked site
// data), so the toggle still works for the rest of the page view.
let memory = false;

function read(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return memory;
  }
}

function subscribe(onChange: () => void) {
  // `storage` fires for other tabs only, so this tab announces its own writes.
  window.addEventListener("storage", onChange);
  window.addEventListener(EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(EVENT, onChange);
  };
}

/**
 * Whether the desktop rail is folded to its icons.
 *
 * Per viewer and per browser, which is all a layout preference needs. Shared
 * through the store rather than props so the loading shell and the real one
 * agree on the width, and the rail does not open and then snap shut the moment
 * the portal finishes loading.
 */
export function useRailCollapsed() {
  const collapsed = useSyncExternalStore(subscribe, read, () => false);

  const setCollapsed = useCallback((next: boolean) => {
    memory = next;
    try {
      if (next) window.localStorage.setItem(KEY, "1");
      else window.localStorage.removeItem(KEY);
    } catch {
      // Storage blocked: `memory` carries it.
    }
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return [collapsed, setCollapsed] as const;
}
